import {
  Checkbox as BaseCheckbox,
  type CheckboxRootChangeEventDetails,
} from "@base-ui/react/checkbox";
import {
  CheckboxGroup as BaseCheckboxGroup,
  type CheckboxGroupChangeEventDetails,
} from "@base-ui/react/checkbox-group";
import { Fieldset } from "@base-ui/react/fieldset";
import classNames from "classnames";
import { Check as CheckIcon, Minus as MinusIcon } from "lucide-react";
import * as React from "react";

import { Field } from "../Field";

import styles from "./Checkbox.module.css";

/**
 * Details accompanying a tick — most usefully `reason`, which says what caused
 * it.
 */
export type CheckboxChangeEventDetails = CheckboxRootChangeEventDetails;
export type CheckboxGroupChangeDetails = CheckboxGroupChangeEventDetails;

/** How big the box and its label text are. */
export type CheckboxSize = "sm" | "md";

const sizeClass: Record<CheckboxSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
};

/**
 * The group a box is an option of, or `null` for a standalone box. A grouped
 * box hangs off the group's `Field.Root` and takes the group's size unless it
 * names its own; a standalone one brings its own field.
 */
const CheckboxGroupContext = React.createContext<{
  size: CheckboxSize;
} | null>(null);

export interface CheckboxProps {
  /** Whether the box is ticked. Omit inside a group, which owns the state. */
  checked?: boolean;

  /** Fired with the new state, and Base UI's event details. */
  onCheckedChange?: (
    checked: boolean,
    eventDetails: CheckboxChangeEventDetails,
  ) => void;

  /** Uncontrolled starting state. @default false */
  defaultChecked?: boolean;

  /** Mixed state, for a box that stands for a set of others. @default false */
  indeterminate?: boolean;

  /** Rendered as a `<label>` around the box, so the text is a click target. */
  label?: React.ReactNode;

  /** A second line under the label. */
  description?: React.ReactNode;

  /** Identifies this box within a `Checkbox.Group`. */
  value?: string;

  disabled?: boolean;
  required?: boolean;

  /** Identifies the field in a native form. */
  name?: string;

  /** Only meaningful standalone; a group carries its own. */
  error?: string;

  /** Reason the box is disabled, shown on hover. */
  title?: string;

  /**
   * The box and label size. Inside a group, defaults to the group's.
   * @default "sm"
   */
  size?: CheckboxSize;

  className?: string;

  /** Accessible name when there is no visible `label`. */
  "aria-label"?: string;
}

/**
 * A single checkbox with its label and description.
 *
 * Standalone it wraps itself in a `Field`; inside a `Checkbox.Group` the group
 * owns the value and this only names one box.
 *
 * @example
 * ```tsx
 * <Checkbox
 *   checked={enabled}
 *   onCheckedChange={setEnabled}
 *   label="Enable on create"
 *   description="The trigger starts firing as soon as it is saved."
 * />
 * ```
 */
function CheckboxRoot({
  checked,
  onCheckedChange,
  defaultChecked,
  indeterminate = false,
  label,
  description,
  value,
  disabled = false,
  required = false,
  name,
  error,
  title,
  size,
  className,
  "aria-label": ariaLabel,
}: CheckboxProps) {
  const group = React.useContext(CheckboxGroupContext);
  const resolvedSize = size ?? group?.size ?? "sm";

  const box = (
    <BaseCheckbox.Root
      className={styles.box}
      checked={checked}
      onCheckedChange={onCheckedChange}
      defaultChecked={defaultChecked}
      indeterminate={indeterminate}
      value={value}
      disabled={disabled}
      required={required}
      name={name}
      aria-label={label === undefined ? ariaLabel : undefined}
    >
      <BaseCheckbox.Indicator className={styles.indicator}>
        {indeterminate ? <MinusIcon aria-hidden /> : <CheckIcon aria-hidden />}
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );

  const option = (
    <Field.Item
      className={classNames(styles.item, sizeClass[resolvedSize], className)}
      title={title}
    >
      {label === undefined ? (
        box
      ) : (
        <Field.Label className={styles.labelRow}>
          {box}
          <span className={styles.labelText}>{label}</span>
        </Field.Label>
      )}
      {description !== undefined && (
        <Field.Description className={styles.description}>
          {description}
        </Field.Description>
      )}
    </Field.Item>
  );

  // Inside a group the surrounding `Field.Root` is the group's. A second one
  // here would shadow it, so the group's invalid state would stop reaching the
  // boxes it applies to.
  if (group !== null) {
    return option;
  }

  return (
    <Field.Root invalid={error !== undefined} disabled={disabled}>
      {option}
      {error !== undefined && <Field.Error match>{error}</Field.Error>}
    </Field.Root>
  );
}

interface CheckboxGroupHandle {
  /** Focus the first box; the group itself cannot take focus. */
  focus: () => void;
}

export interface CheckboxGroupProps {
  /** The ticked boxes, by their `value`. Controlled. */
  value?: string[];

  /** Fired with the new list, and Base UI's event details. */
  onValueChange?: (
    value: string[],
    eventDetails: CheckboxGroupChangeDetails,
  ) => void;

  /** Uncontrolled starting list. */
  defaultValue?: string[];

  /** Names the group, as a fieldset legend. */
  legend?: React.ReactNode;

  /** Helper text under the legend. */
  description?: React.ReactNode;

  /** Group error text, announced once for the whole group. */
  error?: string;

  disabled?: boolean;

  /** Identifies the field in a native form. */
  name?: string;

  /**
   * The size every box in the group takes, unless one names its own.
   * @default "sm"
   */
  size?: CheckboxSize;

  className?: string;

  /** Accessible name when there is no visible `legend`. */
  "aria-label"?: string;

  children?: React.ReactNode;
}

/**
 * A set of checkboxes that share one value, one legend, and one error.
 *
 * @example
 * ```tsx
 * <Checkbox.Group
 *   legend="Change"
 *   description="Which changes fire it. Any checked one does."
 *   value={events}
 *   onValueChange={setEvents}
 * >
 *   {changes.map((change) => (
 *     <Checkbox key={change} value={change} label={eventLabel(change)} />
 *   ))}
 * </Checkbox.Group>
 * ```
 */
const Group = React.forwardRef<CheckboxGroupHandle, CheckboxGroupProps>(
  function Group(
    {
      value,
      onValueChange,
      defaultValue,
      legend,
      description,
      error,
      disabled = false,
      name,
      size = "sm",
      className,
      "aria-label": ariaLabel,
      children,
    },
    ref,
  ) {
    const context = React.useMemo(() => ({ size }), [size]);
    const groupRef = React.useRef<HTMLDivElement | null>(null);
    // The group owns the error — "check at least one" is about the set — but the
    // focus goes to its first box, which is focusable, carries the focus ring,
    // and is the thing to press. The grouping element is none of those: a ref
    // that cannot take focus still answers `.focus()`, so a form library counts
    // it as focused and stops looking.
    React.useImperativeHandle(ref, () => ({
      focus: () => {
        groupRef.current
          ?.querySelector<HTMLElement>('[role="checkbox"]')
          ?.focus();
      },
    }));

    const group = (
      <BaseCheckboxGroup
        ref={groupRef}
        className={styles.group}
        value={value}
        onValueChange={onValueChange}
        defaultValue={defaultValue}
        disabled={disabled}
        aria-label={legend === undefined ? ariaLabel : undefined}
      >
        <CheckboxGroupContext.Provider value={context}>
          {children}
        </CheckboxGroupContext.Provider>
      </BaseCheckboxGroup>
    );

    const described = (
      <>
        {description !== undefined && (
          <Field.Description>{description}</Field.Description>
        )}
        {group}
        {error !== undefined && <Field.Error match>{error}</Field.Error>}
      </>
    );

    return (
      <Field.Root
        className={classNames(styles.field, className)}
        name={name}
        invalid={error !== undefined}
        disabled={disabled}
      >
        {legend === undefined ? (
          described
        ) : (
          <Fieldset.Root className={styles.fieldset}>
            <Fieldset.Legend className={styles.legend}>
              {legend}
            </Fieldset.Legend>
            {described}
          </Fieldset.Root>
        )}
      </Field.Root>
    );
  },
);

export const Checkbox = Object.assign(CheckboxRoot, { Group });
